import { Module } from 'vuex';

import { RootState } from '@/store';
import { Task } from '@/services/task/tasks.types';
import { tasksService } from '@/services/task/tasks.service';
import { formsService } from '@/services/forms/forms.service';

export interface TaskState {
  task: Task;
  taskList: Task[];
  taskLoading: boolean;
}

const state: TaskState = {
  task: null,
  taskList: [],
  taskLoading: false
};

const options: Module<TaskState, RootState> = {
  namespaced: true,
  state: () => state,
  actions: {
    getTask: (
      { commit, dispatch },
      opt: { deviceId: number; repairLocation: string; withCancel: boolean }
    ): Promise<void> => {
      commit('TASK_LOADING', true);
      return tasksService
        .get(opt.deviceId, opt.repairLocation, opt.withCancel)
        .then(taskList => commit('SET_TASK_LIST', taskList))
        .catch(error => error && dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('TASK_LOADING', false));
    },
    getConfTasks: (
      { commit, dispatch },
      opt: { matchId: number; repairLocation: string; withCancel: boolean }
    ): Promise<void> => {
      commit('TASK_LOADING', true);
      return tasksService
        .getConf(opt.matchId, opt.repairLocation, opt.withCancel)
        .then(taskList => commit('SET_TASK_LIST', taskList))
        .catch(error => error && dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('TASK_LOADING', false));
    },
    newTask: ({ commit, dispatch }): Promise<void> => {
      commit('TASK_LOADING', true);
      return tasksService
        .new()
        .then(task => task && commit('SET_TASK', task))
        .catch(error => error && dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('TASK_LOADING', false));
    },
    createTask: ({ commit, dispatch }, task: Task): Promise<void> => {
      commit('TASK_LOADING', true);
      return tasksService
        .create(task)
        .then(task => {
          if (task['item_type'] == 'ItemStatement') commit('SET_TASK', task);
          else commit('ADD_TASK', task);
        })
        .then(() => dispatch('alert/pushSuccess', 'Tache créée avec succes !', { root: true }))
        .catch(error => {
          commit('SET_TASK', task);
          return error && dispatch('alert/pushError', error, { root: true });
        })
        .finally(() => commit('TASK_LOADING', false));
    },
    updateTask: ({ commit, dispatch, rootGetters }, task: Task): Promise<void> => {
      commit('TASK_LOADING', true);
      return tasksService
        .update(task)
        .then(task => {
          if (task['item_type'] == 'Device') {
            dispatch('device/setDevice', task['item_id'], { root: true });
          } else if (task['task_type'] == 'CONF') {
            commit('UPDT_TASK', task);
          } else {
            const deviceId = rootGetters['itemStatement/device'];
            if (deviceId) dispatch('itemStatement/getItemStatementWithCancel', deviceId, { root: true });
            else commit('UPDT_TASK', task);
          }
        })
        .catch(error => error && dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('TASK_LOADING', false));
    },
    deleteTask: ({ commit, dispatch, rootGetters }, taskId: number): Promise<void> => {
      commit('TASK_LOADING', true);
      return tasksService
        .delete(taskId)
        .then(task => {
          if (task['item_type'] == 'Device') {
            dispatch('device/setDevice', task['item_id'], { root: true });
          } else if (task['task_type'] == 'CONF') {
            commit('UPDT_TASK', task);
          } else {
            const deviceId = rootGetters['itemStatement/device'];
            if (deviceId) dispatch('itemStatement/getItemStatementWithCancel', deviceId, { root: true });
            else commit('UPDT_TASK', task);
          }
        })
        .catch(error => error && dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('TASK_LOADING', false));
    },
    validateTask: ({ commit, dispatch, rootGetters }, task: Task): Promise<void> => {
      commit('TASK_LOADING', true);
      return tasksService
        .validate(task)
        .then(task => {
          if (task['item_type'] == 'Device') {
            dispatch('device/setDevice', task['item_id'], { root: true });
          } else if (task['task_type'] == 'CONF') commit('UPDT_TASK', task);
          else {
            const deviceId = rootGetters['itemStatement/device'];
            if (deviceId) dispatch('itemStatement/getItemStatementWithCancel', deviceId, { root: true });
            else commit('UPDT_TASK', task);
          }
        })
        .catch(error => error && dispatch('alert/pushError', error, { root: true }))
        .finally(() => commit('TASK_LOADING', false));
    },
    editTask: ({ commit }, task: Task): void => {
      commit('SET_TASK', task);
    },
    editTaskList: ({ commit }, task: Task): void => {
      commit('editTask', { index: state.taskList.map(t => t.id).indexOf(task.id), task: task });
    },
    clearTask: ({ commit }): void => {
      commit('SET_TASK', null);
      commit('SET_TASK_LIST', []);
    }
  },
  mutations: {
    SET_TASK: (state: TaskState, task: Task) => (state.task = task),
    SET_TASK_LIST: (state: TaskState, taskList: Task[]) => (state.taskList = taskList),
    ADD_TASK: (state: TaskState, task: Task) => state.taskList.push(task),
    UPDT_TASK(state: TaskState, item: Task) {
      const i = state.taskList.findIndex(t => t.id == item.id);
      if (i !== -1) state.taskList.splice(i, 1, item);
    },
    editTask: (state: TaskState, payload: { index; task }) => (state.taskList[payload.index] = payload.task),
    TASK_LOADING: (state: TaskState, taskLoading: boolean) => (state.taskLoading = taskLoading)
  },
  getters: {
    taskCreationForm: (state, getters, rootState) =>
      formsService.getForm('task-creation', state.task, rootState['global-settings'].settings)
  }
};

export default options;
